// src/components/Login.jsx
import React, { useState } from "react";
import { auth, googleProvider } from "./firebase";
import { Eye, EyeOff } from 'lucide-react';
import { signInWithEmailAndPassword, signInWithPopup, sendEmailVerification, signOut } from "firebase/auth";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { notificationService } from "./utils/notificationService";
import "./Login_page.css";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Get location to check for redirect
  const from = location.state?.from || "/HomeScreen"; // Default to HomeScreen if no state
  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setNeedsVerification(false);
    try {
      const { user } = await signInWithEmailAndPassword(auth, formData.email, formData.password);

      if (user.emailVerified) {
        notificationService.success("Welcome Back! 🎉", "You have successfully logged in to your account.");
        console.log(`LOGIN: Navigating to ${from} after successful login`);
        navigate(from, { state: { name: user.displayName || "User", email: user.email } });
      } else {
        await signOut(auth); // don't keep an unverified session
        setNeedsVerification(true);
        notificationService.warning("Email Verification Required", "Please verify your email first. Check your inbox and spam folder.");
      }
    } catch (error) {
      notificationService.error("Login Failed", error.message || "An error occurred during login. Please try again.");
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, formData.email, formData.password);

      if (user.emailVerified) {
        navigate(from); // they verified in the meantime
        return;
      }
      await sendEmailVerification(user);
      await signOut(auth);
      notificationService.success("Email Sent", "A new verification link is on its way.");
    } catch (error) {
      const msg = error.code === "auth/too-many-requests"
        ? "Too many attempts. Please wait a few minutes and try again."
        : error.message;
      notificationService.error("Could not resend", msg);
    } finally {
      setResending(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      notificationService.success("Welcome! 🎉", `Successfully logged in with Google as ${user.displayName || user.email}`);
      console.log(`GOOGLE LOGIN: Navigating to ${from} after successful Google login`);
      navigate(from, { state: { name: user.displayName, email: user.email } });
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        notificationService.info("Login Cancelled", "Google login was cancelled. You can try again anytime.");
      } else {
        notificationService.error("Google Login Failed", error.message || "Failed to login with Google. Please try again.");
      }
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  return (
    <div className="login-container">
      <div className="form-box">

        <span className="back-button-login" onClick={handleBack}>
                  <img src="https://img.icons8.com/ios-filled/30/ffffff/left.png" alt="Back" />
                </span>
                <h2 className="welcome-text">Welcome Back</h2>


        <button className="google-btn" onClick={handleGoogleLogin}>
          <img src="https://img.icons8.com/color/16/000000/google-logo.png" alt="Google" />
          Login with Google
        </button>

        <p className="divider">or Login with Email</p>

        <form onSubmit={handleLogin}>
          <div className="input-wrapper">
            <img src={require("./mail.png")} alt="Email Icon" />
            <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required />
          </div>

          <div className="input-wrapper">
            <img src={require("./lock.png")} alt="Lock Icon" />
            <input type={showPassword ? "text" : "password"} name="password" placeholder="Password" value={formData.password} onChange={handleChange} required />
            <span className={`toggle-icon ${showPassword ? 'show' : ''}`}
              onClick={togglePassword}>
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>
          <a href="/Forget_password" className="forgot-password-link">Forgot Password?</a>
          <button type="submit" className="submit-btn">Login</button>

          {needsVerification && (
            <div className="verify-box">
              <p>Your email isn't verified yet. Click the link we sent, then log in again.</p>
              <button type="button" className="resend-btn" onClick={handleResendVerification} disabled={resending}>
                {resending ? "Sending..." : "Resend verification email"}
              </button>
            </div>
          )}
        </form>

        <p className="policy-text">
          Don’t have an account? <Link to="/Get_started">Sign Up</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;